import React, { useEffect, useState } from 'react';
import MarketInsight from '@app/model/market_insight/MarketInsight';
import MarketRegionStateCluster, {
	marketRegionStateClusterBgClasses,
	marketRegionStateClusterNames,
} from '@app/model/market_area/MarketRegionStateCluster';
import { MarketRegionStateService } from '@app/model/market_area/service/MarketRegionStateService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { ArrowDownIcon, ArrowUpIcon, MinusIcon } from '@heroicons/react/20/solid';
import Portlet from '@app/component/portlet/Portlet';
import Utilities from '@util/Utilities';

type MarketAreaStatesComparison = {
	currentCluster: MarketRegionStateCluster | undefined,
	previousCluster: MarketRegionStateCluster | undefined,
	previousClusterDaysBefore: number | undefined,
	difference: number | undefined
}

// regionName: set when the region has 2 or more markets. The card reads the region, so it shows the region name.
const MarketInsightMarketDemandFragment: React.FunctionComponent<{
	marketInsight: MarketInsight,
	statDate: string,
	regionName?: string | null
}> = ({ marketInsight, statDate, regionName }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const [comparison, setComparison] = useState<MarketAreaStatesComparison>();

	useEffect(() => {
		const fetchData = async () => {
			const currentMarketAreaState = marketInsight.findMarketAreaStateByStatDate(statDate);

			let comparison: MarketAreaStatesComparison = {
				currentCluster: currentMarketAreaState?.cluster,
				previousCluster: undefined,
				previousClusterDaysBefore: undefined,
				difference: undefined,
			};

			comparison = await addComparison(comparison);

			setComparison(comparison);
		};

		fetchData();

	}, [marketInsight, statDate]);

	const addComparison = async (comparison: MarketAreaStatesComparison): Promise<MarketAreaStatesComparison> => {
		try {
			const previous = await MarketRegionStateService.findPreviousMarketAreaState({
				companyHotelId: companyHotel?.id,
				statDate,
			});

			// GraphQL can send a null cluster: no comparison then, as before.
			if (previous !== undefined && previous !== null && previous.cluster !== undefined && previous.cluster !== null
				&& comparison.currentCluster !== undefined && comparison.currentCluster !== null) {
				const updatedComparison = { ...comparison };
				updatedComparison.previousCluster = previous.cluster;
				const clusterValues = Object.values(MarketRegionStateCluster);
				const currentIndex = clusterValues.indexOf(comparison.currentCluster);
				const previousIndex = clusterValues.indexOf(previous.cluster);
				updatedComparison.difference = currentIndex - previousIndex;
				updatedComparison.previousClusterDaysBefore = Utilities.daysDifferenceFromNow(previous.creationDate);
				return updatedComparison;
			}
		} catch (e) {
			// ignore
		}
		return comparison;
	};

	if (!comparison || !comparison.currentCluster) {
		return <Portlet title="Market Demand" note={regionNote(regionName)}>
			<p className="block text-xs font-light text-gray-400">Market demand not found!</p>
		</Portlet>;
	}

	return <Portlet title="Market Demand" note={regionNote(regionName)}>
		<div className="grow">
			<div className="flex items-center gap-x-2">
				<div className="flex flex-wrap items-center gap-x-1">
						<span
							className={`inline-flex rounded-full w-3 h-3 ${marketRegionStateClusterBgClasses[comparison.currentCluster]}`} />
					<p
						className="text-lg font-semibold text-gray-900">{marketRegionStateClusterNames[comparison.currentCluster]}</p>
				</div>
				{comparison.difference !== undefined && comparison.difference > 0 &&
					<div
						className="inline-flex items-baseline rounded-full bg-green-100 px-1 py-1 text-sm font-medium text-green-800">
						<ArrowUpIcon className="w-3 h-3 shrink-0 self-center text-green-500" />
					</div>}
				{comparison.difference !== undefined && comparison.difference < 0 &&
					<div
						className="inline-flex items-baseline rounded-full bg-red-100 px-1 py-1 text-sm font-medium text-red-800">
						<ArrowDownIcon className="w-3 h-3 shrink-0 self-center text-red-500" />
					</div>}
				{comparison.difference === 0 &&
					<div
						className="inline-flex items-baseline rounded-full bg-gray-100 px-1 py-1 text-sm font-medium text-gray-800">
						<MinusIcon className="w-3 h-3 shrink-0 self-center text-gray-500" />
					</div>}
			</div>
			{!!comparison.previousCluster &&
				<p className="block text-xs font-light text-gray-400">Demand was {comparison.previousClusterDaysBefore} days
					ago: &nbsp;
					<span
						className="font-medium text-gray-500">{marketRegionStateClusterNames[comparison.previousCluster]}</span>
				</p>}
		</div>
	</Portlet>;
};

const regionNote = (regionName?: string | null): React.ReactNode =>
	regionName !== undefined && regionName !== null ? <>{regionName} region</> : undefined;

export default MarketInsightMarketDemandFragment;
